import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { normalizeModules } from '../utils/appModules';
import { authJsonHeaders } from '../utils/authHeaders';
import {
  CaisseModuleIcon,
  FacturationModuleIcon,
  DeclarationModuleIcon,
  PaieModuleIcon,
} from './DashboardModuleIcons';
import './DashboardHomePage.css';

const DECLARATIONS_URL = 'https://tltd-cgm.codeiums.com/';

const MODULES = [
  {
    id: 'caisse',
    index: '01',
    category: 'CAISSE',
    name: 'Caisse CGM',
    subtitle: 'Encaissements & décaissements',
    description: 'Ouvre l’application caisse du cabinet.',
    path: '/clients',
    Icon: CaisseModuleIcon,
  },
  {
    id: 'honoraires',
    index: '02',
    category: 'NH',
    name: 'Notes d’honoraires',
    subtitle: 'Facturation NH',
    description: 'Ouvre le module notes d’honoraires.',
    path: '/nh',
    state: { pickMatricule: true },
    Icon: FacturationModuleIcon,
  },
  {
    id: 'declarations',
    index: '03',
    category: 'DÉCLARATIONS',
    name: 'Déclarations',
    subtitle: 'Espace déclaratif',
    description: 'Ouvre la page de déclarations.',
    href: DECLARATIONS_URL,
    Icon: DeclarationModuleIcon,
  },
  {
    id: 'paie',
    index: '04',
    category: 'PAIE',
    name: 'Paie & personnel',
    subtitle: 'Bientôt disponible',
    description: 'Module vide pour le moment.',
    disabled: true,
    Icon: PaieModuleIcon,
  },
];

export default function DashboardHomePage() {
  const navigate = useNavigate();
  const { activeCabinet, setActiveCabinet, isAdmin } = useAuth();

  useEffect(() => {
    if (typeof isAdmin === 'function' && isAdmin()) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/cabinets', { headers: authJsonHeaders() });
        const data = await res.json();
        if (cancelled || !res.ok || !data.success) return;
        const list = Array.isArray(data.cabinets) ? data.cabinets : [];
        if (!list.length) return;
        const currentId = activeCabinet?.id != null ? Number(activeCabinet.id) : null;
        const match =
          (currentId && list.find((c) => Number(c.id) === currentId)) || list[0];
        if (match) setActiveCabinet(match);
      } catch (_) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh on mount / cabinet id
  }, [activeCabinet?.id, isAdmin, setActiveCabinet]);

  const visibleModules = useMemo(() => {
    if (typeof isAdmin === 'function' && isAdmin()) {
      return MODULES;
    }
    const allowed = normalizeModules(activeCabinet?.allowed_modules, {
      defaultAll: false,
    });
    return MODULES.filter((m) => allowed.includes(m.id));
  }, [activeCabinet, isAdmin]);

  const openModule = ({ path, state, href, disabled }) => {
    if (disabled) return;
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!path) return;
    navigate(path, state ? { state } : undefined);
  };

  // Après login depuis une carte Caisse / NH sur la landing
  useEffect(() => {
    let pending = null;
    try {
      pending = sessionStorage.getItem('pendingModule');
      if (pending) sessionStorage.removeItem('pendingModule');
    } catch (_) {
      return;
    }
    if (!pending) return;
    const mod = MODULES.find((m) => m.id === pending);
    if (!mod || mod.disabled) return;
    if (visibleModules.some((m) => m.id === mod.id)) {
      openModule(mod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleModules]);

  const cabinetLabel =
    activeCabinet?.code || activeCabinet?.name || activeCabinet?.label || '';
  const cabinetCardLabel =
    activeCabinet?.name || activeCabinet?.code || activeCabinet?.label || '';

  return (
    <div className="dashboard-home-page">
      <div className="dashboard-home-page__toolbar">
        <button
          type="button"
          className="dashboard-home-page__retour"
          onClick={() => navigate('/cabinets')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Retour
        </button>
      </div>

      <div className="dashboard-home-page__inner">
        <header className="dashboard-home-page__header">
          <div className="dashboard-home-page__eyebrow">
            <span>Applications</span>
          </div>
          <h1 className="dashboard-home-page__title">Choisissez un module</h1>
          <p className="dashboard-home-page__subtitle">
            {cabinetLabel
              ? `Cabinet ${cabinetLabel} — Caisse & NH dans l’app, déclarations sur leur page, paie bientôt.`
              : 'Caisse & NH dans l’app, déclarations sur leur page, paie bientôt.'}
          </p>
        </header>

        {visibleModules.length === 0 ? (
          <div className="module-placeholder">
            <h2>Aucun module autorisé</h2>
            <p>
              Aucun module n&apos;est activé pour votre compte sur ce cabinet.
              Contactez un administrateur.
            </p>
          </div>
        ) : (
          <div
            className={`dashboard-home-page__grid dashboard-home-page__grid--n${visibleModules.length}`}
          >
            {visibleModules.map((mod) => {
              const Icon = mod.Icon;
              return (
                <button
                  key={mod.id}
                  type="button"
                  className={`dashboard-home-card dashboard-home-card--${mod.id}${mod.disabled ? ' is-disabled' : ''}`}
                  onClick={() => openModule(mod)}
                  disabled={Boolean(mod.disabled)}
                  aria-label={mod.name}
                >
                  <span className="dashboard-home-card__banner">
                    <span className="dashboard-home-card__banner-icon" aria-hidden="true">
                      <Icon />
                    </span>
                    <span className="dashboard-home-card__banner-cat">{mod.category}</span>
                  </span>
                  <span className="dashboard-home-card__name">{mod.name}</span>
                  <span className="dashboard-home-card__sub">{mod.subtitle}</span>
                  <span className="dashboard-home-card__desc">{mod.description}</span>
                  <span className="dashboard-home-card__cta" aria-hidden="true">
                    {mod.disabled ? 'Bientôt disponible' : 'Ouvrir'}
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14" />
                      <path d="M13 6l6 6-6 6" />
                    </svg>
                  </span>
                  {cabinetCardLabel ? (
                    <span className="dashboard-home-card__cabinet" title={cabinetCardLabel}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                        <path d="M3 21h18M5 21V5l7-3 7 3v16M9 21v-4h6v4M8 8h1M15 8h1M8 12h1M15 12h1" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {cabinetCardLabel}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
